import { useState } from "react";
import { motion } from "framer-motion";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { formatClock } from "@/lib/export";
import { monoMetaClass, secondaryButtonClass } from "@/lib/ui";

/**
 * Timed transcript, PLAN.md §8 (Phase 8).
 *
 * `transcripts.segments` is a `take`, not a paginated query, so paging happens
 * by mounting one child per page: each holds its own live subscription from a
 * fixed `fromIndex`, and the last one grows the list when it is full. A
 * 90-minute lecture is ~1500 segments and rendering all of them on open is a
 * second of layout for text that is mostly below the fold.
 */

const PAGE = 200;

export default function TranscriptView({
  recordingId,
  currentMs,
  onSeek,
  filter,
}: {
  recordingId: Id<"recordings">;
  /** Playhead, for the highlight. Undefined when nothing is playing. */
  currentMs?: number;
  onSeek?: (ms: number) => void;
  /** Case-insensitive substring over the loaded pages; empty shows everything. */
  filter?: string;
}) {
  // Keyed by recording so switching to another one starts at page 1 without an
  // effect: a different recording is a different list.
  const [paging, setPaging] = useState({ id: recordingId, pages: 1 });
  const pages = paging.id === recordingId ? paging.pages : 1;

  return (
    <div className="flex flex-col gap-1">
      {Array.from({ length: pages }, (_, page) => (
        <SegmentPage
          key={page}
          recordingId={recordingId}
          fromIndex={page * PAGE}
          isLast={page === pages - 1}
          currentMs={currentMs}
          onSeek={onSeek}
          filter={filter}
          onMore={() => setPaging({ id: recordingId, pages: pages + 1 })}
        />
      ))}
    </div>
  );
}

function SegmentPage({
  recordingId,
  fromIndex,
  isLast,
  currentMs,
  onSeek,
  filter,
  onMore,
}: {
  recordingId: Id<"recordings">;
  fromIndex: number;
  isLast: boolean;
  currentMs?: number;
  onSeek?: (ms: number) => void;
  filter?: string;
  onMore: () => void;
}) {
  const segments = useQuery(api.transcripts.segments, {
    recordingId,
    fromIndex,
    limit: PAGE,
  });

  if (segments === undefined) {
    return <p className={monoMetaClass}>Loading transcript…</p>;
  }
  if (segments.length === 0 && fromIndex === 0) {
    return <p className={monoMetaClass}>No transcript segments for this recording.</p>;
  }

  const needle = (filter ?? "").trim().toLowerCase();
  const visible =
    needle === ""
      ? segments
      : segments.filter((segment) => segment.text.toLowerCase().includes(needle));

  return (
    <>
      {visible.map((segment) => {
        const playing =
          currentMs !== undefined &&
          currentMs >= segment.startMs &&
          currentMs < segment.endMs;
        return (
          <button
            key={segment._id}
            type="button"
            onClick={() => onSeek?.(segment.startMs)}
            className={[
              "group relative flex w-full items-baseline gap-3 rounded-lg px-2.5 py-1 text-left transition-colors duration-150",
              onSeek === undefined ? "cursor-default" : "cursor-pointer",
              playing
                ? "bg-neutral-900/[0.06] dark:bg-white/[0.08]"
                : "hover:bg-[#fafaf8] dark:hover:bg-[#1a1a19]",
            ].join(" ")}
          >
            {playing && (
              <motion.span
                layoutId="transcript-active-bar"
                className="absolute left-0 top-1 bottom-1 w-[2.5px] rounded-full bg-neutral-900 dark:bg-neutral-100"
                transition={{ type: "spring", stiffness: 450, damping: 35 }}
              />
            )}
            <span
              className={[
                "shrink-0 font-mono text-[11px] tabular-nums transition-colors duration-150",
                playing
                  ? "font-medium text-[#222] dark:text-[#ededeb]"
                  : "text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300",
              ].join(" ")}
            >
              {formatClock(segment.startMs)}
            </span>
            <span
              className={[
                "text-sm leading-relaxed transition-colors duration-150",
                playing
                  ? "text-[#111] dark:text-white"
                  : "text-[#333] dark:text-[#d7d7d3]",
              ].join(" ")}
            >
              {segment.text}
            </span>
          </button>
        );
      })}

      {/* A full page means the index did not run out, so there may be another. */}
      {isLast && segments.length === PAGE && (
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          className={`${secondaryButtonClass} no-print mt-2 self-start`}
          onClick={onMore}
        >
          Load more
        </motion.button>
      )}
    </>
  );
}
